import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            try {
              const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))
              // check for previous reload time not to reload infinitely
              if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
                window.sessionStorage.setItem('nuxt-reload', timeNow)
                window.location.reload(true /* skip cache */)
              }
            } catch {
              // don't throw an error if we have issues reading sessionStorage
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"baseUrl":"https://film.gala.com","gameItemProductsGame":"Gala Film","googleOptimizeId":"OPT-MMBVRNV","flaggedGamesToInclude":"Town Crush","townCrushLink":"https://super-secret-project.gala.games/","showTownCrushPage":true,"gcActionsDisabled":true,"datadogRumPlugin":{"enabled":true,"applicationId":"3ca51586-5284-4420-82f8-0d7a4318e6dd","clientToken":"pub409ff15c588c911da841e94cdb7af427"},"userAttributionPlugin":{"adaptors":{"debugAdaptor":{"enabled":false},"metaAdaptor":{"enabled":true,"accountId":"264112280030049"},"xAdaptor":{"enabled":true,"accountId":"o9avg"},"firebaseAdaptor":{"enabled":true,"apiKey":"AIzaSyDATNUE4X3-EM_CBYfrkiDQ34PPo_2teLo","authDomain":"gala-film.firebaseapp.com","projectId":"gala-film","storageBucket":"gala-film.appspot.com","messagingSenderId":"677771973879","appId":"1:677771973879:web:e420e257a99f6b303d2ff2","measurementId":"G-5C4CRM08L2"},"gtmAdaptor":{"enabled":true},"activeCampaignAdaptor":{"enabled":true,"accountId":"91470544"},"addShoppersAdaptor":{"enabled":true,"accountId":"644f842bf590da04d0620bb7"},"galaAnalyticsAdaptor":{"enabled":true,"publisherId":"Y3UyIHRnLgOVEASw4cYU9J2pNeJSacAZ5VJxVb0hOAoSbex4z9nMJnRYl80V41SP","publisherGroup":"product","reportingEndpoint":"https://alpha-data.gala.com","fpGamesEndpoint":"https://fp.gala.games","fpComEndpoint":"https://fp.gala.com","environment":"prod"}}},"fortitudeIsReleased":true,"townCrushIsReleased":true,"townCrushIsReleasedInternally":true,"showBattlestarGalactica":true,"showEternalParadox":true,"showGrit":true,"sanitySiteConfigSlug":"global-settings","sanityProjectId":"roa6t757","sanityDataset":"production","sanityApiVersion":"2022-05-24","sanityToken":"","sanityUseCdn":true,"sanityWithCredentials":false,"galaBaseDomain":"gala.com","galaCompanyLink":"https://gala.com","orderPaymentContractAddress":"0x50343Db63b24981397C6411Ae2e4A11Bf5f69D4B","ethereumBridgeContractAddress":"0x9f452b7cC24e6e6FA690fe77CF5dD2ba3DbF1ED9","w3wConnectionEnabled":true,"galaItemContractAddress":"0xc36cF0cFcb5d905B8B513860dB0CFE63F6Cf9F5c","spiderTanksItemContractAddress":"null","voxContractAddress":"0xad9Fd7cB4fC7A0fBCE08d64068f60CbDE22Ed34C","twdVoxContractAddress":"0x338BE3d8D0209815601e72F7A04Ac7F37d61564b","mirandusVoxContractAddress":"0xf76179bb0924BA7da8E7b7Fc2779495D7A7939d8","curryContractAddress":"0x9cA3A9a3aA59C7ddd61C29f6b0540ad9988AeDE6","blueRealmContractAddress":"0x2378e88624c7DfDF73A6B02dEd8733449916FDBa","ethNodeUrl":"https://eth8.gala.games","nodeDashboardUrl":"https://node.gala.com","galaGyriProductId":"638e75c417ab57e7e016e939","staticTcItemBaseIds":["0x05cd00000000000000000000000000000000"],"referralDomains":".gala.com","referralCookiePrefix":"","muxCurrentViewsRestriction":"MUX_CURRENT_VIEWS_BY_TOKEN","muxTotalViewsRestriction":"MUX_VIDEO_TOTAL_VIEWS","discordRedirectUri":"https://film.gala.com","videoTotalCountDisplayThreshold":100,"videoProgressTrackingIntervalMs":5000,"endOfVideoResumeCutoffThresholdMs":15000,"auth0Domain":"auth.gala.com","auth0MfaDomain":"galagames-prod.us.auth0.com","auth0ClientId":"j9Mo6aow1wXiFfW5KMdCdyQ6MM29Yh99","auth0Audience":"https://app.gala.games/platformserver","disabledW3WProviders":[],"w3wProviderWhitelist":[],"stripePublicApiKey":"pk_live_51LW0K2BYKfWsrZNEn1lV4HQJRhuuhp52BoEd40bryxaPyBKczezWrkWeufuoMlQml8outGLN1rAHSYqTpnoBruLy00lGmvFhOk","creditCardMaxPurchase":5500,"filmNodeEgress":"https://film-egress-entertainment-film-prod-gala.gala.com/api/v1/egress","muxDebug":false,"batchActions":true,"brand":"gala","discordConnectLink":"https://discord.gala.games/authorize?redirect_uri=https://film.gala.com","displayGameInIframe":true,"echosOfEmpireLatestVersion":"1.0.0","echosOfEmpireIsReleased":false,"echosOfEmpireIsReleasedWin":false,"echosOfEmpireIsReleasedMac":false,"fortitudeIsReleasedWin":false,"fortitudeIsReleasedMac":false,"fortitudeLatestVersion":"0.5.2","galaChainMigrationDate":"1642154400000","galaLauncherIntallerUrlMac":"https://static.gala.games/installers/GalaLauncher-installer-mac-1.0.9.pkg","galaLauncherIntallerUrlWin":"https://static.gala.games/installers/GalaLauncherSetup-1.0.9.exe","galaLauncherLatestVersion":"1.0.9","galaLauncherPort":"32123","galaverseTicketsIsReleased":true,"gatewayUri":"https://walletsrv.gala.games/gateway","gtmContainerId":"GTM-T657TRZ","isProd":true,"mirandusLatestVersion":"0.0.84.0","mirandusIsReleased":true,"mirandusIsReleasedWin":true,"mirandusIsReleasedMac":false,"networkType":"prod","nodeEnv":"production","recaptchaSitekey":"6Ldv1YUdAAAAAKHd_Byy2mwkMpKSwaKQK5OXJWWT","server":"https://walletsrv.gala.games/graphql","showConvertCoinScreens":false,"showBuyCoinScreens":"true","spiderTanksLatestVersion":"0.6.4.126","spiderTanksIsReleased":true,"spiderTanksIsReleasedWin":true,"spiderTanksIsReleasedMac":false,"spiderTanksEarlyAccessStart":"1631815200000","spiderTanksEarlyAccessEnd":"1631901600000","townStarLink":"https://townstar.sandbox-games.com/launch","townStarEmphasizedStoreCategory":"skins","townStarIsReleased":true,"legacyIsReleased":false,"legacyIsReleasedWin":false,"legacyIsReleasedMac":false,"lastExpeditionIsReleased":false,"legendsRebornIsReleased":false,"legendsRebornIsReleasedWin":true,"legendsRebornIsReleasedMac":true,"legendsRebornLatestVersion":"0.2.1.738852","displayTSNodeTimestamp":"1637708400000","walkingDeadIsReleased":false,"walkingDeadIsReleasedWin":false,"walkingDeadIsReleasedMac":false,"superiorIsReleased":true,"superiorIsReleasedWin":false,"superiorIsReleasedMac":false,"superiorLatestVersion":"0.0.5","twdVoxBaseId":"0x02fe00000000000000000000000000000000","siteConfigFromEnv":{"canShowDebugTool":false,"debugToolQueryParamValue":"hnp49VUyfh4dEV","isMarketingMode":false,"priorityPassBaseId":"0x04e500000000000000000000000000000000","projectPageVariation":"v2","shouldShowTokenBalanceMoveNote":true,"shouldShowFilmNodeBuyPage":true,"shouldShowLandingCarousel":false,"shouldShowNodesInInventory":true,"shouldShowGetGala":true,"shouldShowLanguageSwitcher":false,"shouldShowPriorityPassExchange":false,"shouldPollForSaleUpdates":false,"saleItemPollInterval":60},"pubnubPublishKey":"pub-c-315629ec-baee-42ad-82fa-30440fcaaa96","pubnubSubscribeKey":"sub-c-d35d0099-03e0-4ac1-bb83-035fb3dda4f2","pubnubChatEnv":"prod","gasFallbackAmount":"55000","gasMaximumLimit":"21000","gasEstimateWaitMs":"500","restrictionsServiceEndpoint":"https://prod-restrictions-service.gala.com","filmApiServiceEndpoint":"https://film-api-entertainment-film-prod-gala.gala.com/api/v1","galaNodeUrl":"https://node.gala.com","musicDataEndpoint":"https://prod-gala-music-data.music.gala.com","musicDataContractId":"0x63a407Bcb34983A5bA683f7555Fdfa24DAD4f18D","dcdnMuxDataEnvKey":"gtvdtvbhqmjq7ea5dj6mf81fi"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
